import React from 'react';
import { MaintenanceModel } from './useMaintenanceData';
import { SERVICES, STATUS_OPTIONS } from './maintenanceTypes';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const fieldStyle: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'var(--rp-text)',
  border: '1px solid rgba(255,255,255,0.15)',
};

const labelCls = 'block text-sm font-semibold mb-1';
const fieldCls = 'w-full px-3 py-2 rounded-lg text-sm';

const MaintenanceDialogs: React.FC<{ model: MaintenanceModel }> = ({ model }) => {
  const {
    showMaintenanceForm, setShowMaintenanceForm, form, setForm, saving,
    editStatusService, setEditStatusService, editStatusValue, setEditStatusValue,
    editStatusMessage, setEditStatusMessage, showScheduleForm, setShowScheduleForm,
    scheduleForm, setScheduleForm, deleteConfirm, setDeleteConfirm,
    handleEnableMaintenance, handleSetServiceStatus, handleScheduleMaintenance,
    handleDeleteWindow, toggleFormService,
  } = model;

  return (
    <>
      {/* Enable Maintenance Modal */}
      {showMaintenanceForm && (
        <AdminModal ariaLabel="Enable maintenance mode" onCancel={() => setShowMaintenanceForm(false)} maxWidth="32rem">
          <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>Enable Maintenance Mode</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
            This will set all selected services to maintenance status and create a maintenance window visible on the Status Page.
          </p>
          <div className="space-y-4">
            <div>
              <label htmlFor="mnt-title" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Title</label>
              <input id="mnt-title" type="text" value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Database Upgrade"
                className={fieldCls}
                style={fieldStyle}
              />
            </div>
            <div>
              <label htmlFor="mnt-description" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Description</label>
              <textarea id="mnt-description" value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's being done?"
                rows={2}
                className={`${fieldCls} resize-y`}
                style={fieldStyle}
              />
            </div>
            <div>
              <label htmlFor="mnt-duration" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Expected Duration</label>
              <div className="flex gap-2 items-center">
                <input id="mnt-duration" type="number" value={form.duration_hours} min="0.5" step="0.5"
                  onChange={e => setForm(prev => ({ ...prev, duration_hours: e.target.value }))}
                  className="w-24 px-3 py-2 rounded-lg text-sm"
                  style={fieldStyle}
                />
                <span className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>hours</span>
              </div>
            </div>
            <div>
              <span className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Affected Services</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Affected services">
                {SERVICES.map(svc => (
                  <button key={svc}
                    onClick={() => toggleFormService(svc, 'maintenance')}
                    className="px-3 py-1.5 rounded-lg text-sm capitalize transition-all"
                    style={{
                      backgroundColor: form.services.includes(svc) ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.05)',
                      color: form.services.includes(svc) ? '#f59e0b' : 'var(--rp-text-secondary)',
                      border: `1px solid ${form.services.includes(svc) ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {svc}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowMaintenanceForm(false)}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
            <button onClick={handleEnableMaintenance} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: 'var(--admin-warning-strong)' }}
            >
              {saving ? 'Enabling...' : 'Enable Maintenance'}
            </button>
          </div>
        </AdminModal>
      )}

      {/* Schedule Maintenance Modal */}
      {showScheduleForm && (
        <AdminModal ariaLabel="Schedule maintenance" onCancel={() => setShowScheduleForm(false)} maxWidth="32rem">
          <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>Schedule Maintenance</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="mnt-sched-title" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Title</label>
              <input id="mnt-sched-title" type="text" value={scheduleForm.title}
                onChange={e => setScheduleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Scheduled Database Migration"
                className={fieldCls}
                style={fieldStyle}
              />
            </div>
            <div>
              <label htmlFor="mnt-sched-description" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Description</label>
              <textarea id="mnt-sched-description" value={scheduleForm.description}
                onChange={e => setScheduleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Details about the maintenance"
                rows={2}
                className={`${fieldCls} resize-y`}
                style={fieldStyle}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="mnt-sched-date" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Date</label>
                <input id="mnt-sched-date" type="date" value={scheduleForm.start_date}
                  onChange={e => setScheduleForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className={fieldCls}
                  style={fieldStyle}
                />
              </div>
              <div>
                <label htmlFor="mnt-sched-time" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Time</label>
                <input id="mnt-sched-time" type="time" value={scheduleForm.start_time}
                  onChange={e => setScheduleForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className={fieldCls}
                  style={fieldStyle}
                />
              </div>
            </div>
            <div>
              <label htmlFor="mnt-sched-duration" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Duration</label>
              <div className="flex gap-2 items-center">
                <input id="mnt-sched-duration" type="number" value={scheduleForm.duration_hours} min="0.5" step="0.5"
                  onChange={e => setScheduleForm(prev => ({ ...prev, duration_hours: e.target.value }))}
                  className="w-24 px-3 py-2 rounded-lg text-sm"
                  style={fieldStyle}
                />
                <span className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>hours</span>
              </div>
            </div>
            <div>
              <span className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Affected Services</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Affected services">
                {SERVICES.map(svc => (
                  <button key={svc}
                    onClick={() => toggleFormService(svc, 'schedule')}
                    className="px-3 py-1.5 rounded-lg text-sm capitalize transition-all"
                    style={{
                      backgroundColor: scheduleForm.services.includes(svc) ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                      color: scheduleForm.services.includes(svc) ? '#3b82f6' : 'var(--rp-text-secondary)',
                      border: `1px solid ${scheduleForm.services.includes(svc) ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {svc}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setScheduleForm(prev => ({ ...prev, is_published: !prev.is_published }))}
                className="admin-pubtoggle"
                aria-label="Toggle visibility on Status Page"
              >
                <span
                  className="admin-pubtoggle-track"
                  style={{ width: 48, height: 24, backgroundColor: scheduleForm.is_published ? 'var(--admin-success)' : 'var(--admin-neutral-track)' }}
                >
                  <span
                    className="admin-pubtoggle-knob"
                    style={{ width: 20, height: 20, transform: scheduleForm.is_published ? 'translateX(24px)' : 'translateX(0)' }}
                  />
                </span>
              </button>
              <span className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>
                {scheduleForm.is_published ? 'Visible on Status Page' : 'Hidden from Status Page'}
              </span>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowScheduleForm(false)}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
            <button onClick={handleScheduleMaintenance} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: 'var(--admin-primary)' }}
            >
              {saving ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </AdminModal>
      )}

      {/* Edit Service Status Modal */}
      {editStatusService && (
        <AdminModal ariaLabel={`Update ${editStatusService} status`} onCancel={() => setEditStatusService(null)}>
          <h3 className="text-lg font-bold mb-4 capitalize" style={{ color: 'var(--rp-text)' }}>
            Update {editStatusService} Status
          </h3>
          <div className="space-y-4">
            <div>
              <span className="block text-sm font-semibold mb-2" style={{ color: 'var(--rp-text-secondary)' }}>Status</span>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label="Service status">
                {STATUS_OPTIONS.map(opt => (
                  <button key={opt.value}
                    onClick={() => {
                      setEditStatusValue(opt.value);
                      setEditStatusMessage(opt.defaultMessage);
                    }}
                    className="px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                    style={{
                      backgroundColor: editStatusValue === opt.value ? `${opt.color}30` : 'rgba(255,255,255,0.05)',
                      color: editStatusValue === opt.value ? opt.color : 'var(--rp-text-secondary)',
                      border: `1px solid ${editStatusValue === opt.value ? opt.color : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="mnt-status-message" className={labelCls} style={{ color: 'var(--rp-text-secondary)' }}>Message (optional)</label>
              <input id="mnt-status-message" type="text" value={editStatusMessage}
                onChange={e => setEditStatusMessage(e.target.value)}
                placeholder="e.g. Experiencing intermittent timeouts"
                className={fieldCls}
                style={fieldStyle}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setEditStatusService(null)}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
            <button onClick={handleSetServiceStatus}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ backgroundColor: 'var(--admin-primary)' }}
            >
              Update Status
            </button>
          </div>
        </AdminModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <AdminModal ariaLabel="Delete maintenance window" onCancel={() => setDeleteConfirm(null)}>
          <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--rp-text)' }}>Delete Window?</h3>
          <p className="text-sm mb-6" style={{ color: 'var(--rp-text-secondary)' }}>
            This will permanently remove this maintenance window. This cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
            <button onClick={() => handleDeleteWindow(deleteConfirm)}
              className="admin-btn admin-btn-danger"
            >
              Delete
            </button>
          </div>
        </AdminModal>
      )}
    </>
  );
};

export default MaintenanceDialogs;
